import './host'

import { GatewayService } from '@graft/nuget-gc.webportals.gateways'

import { getSessionToken } from '@common/core/helpers'

import { gatewayToObject } from './objects'
import type { Gateway } from './types/gateway.type'
import { invokeRemoveGateway } from './utils/invokeRemoveGateway'

export async function getGateways(projectId: string): Promise<Gateway[]> {
  if (!projectId) {
    return []
  }

  try {
    const token = await getSessionToken()
    const gateways = await GatewayService.getGateways(token, projectId)

    return Promise.all(gateways.map((g) => gatewayToObject(g)))
  } catch (error) {
    console.error('Failed to get gateways:', error)
    return []
  }
}

export async function addNewGateway(projectId: string, name = ''): Promise<string | null> {
  try {
    const token = await getSessionToken()
    const gatewayId = await GatewayService.addNewGateway(token, projectId, name)
    return gatewayId ?? null
  } catch (error) {
    console.error('Failed to create gateway:', error)
    throw error
  }
}

export async function removeGateway(gatewayId: string): Promise<void> {
  if (!gatewayId) {
    throw new Error('Gateway id is required')
  }

  try {
    await invokeRemoveGateway(gatewayId)
  } catch (error) {
    console.error('Failed to remove gateway:', error)
    throw error
  }
}

const ugmInFlight = new Map<string, Promise<boolean>>()

export function didGatewayReportAnyUgm(gatewayKey: string): Promise<boolean> {
  const inflight = ugmInFlight.get(gatewayKey)
  if (inflight) return inflight

  const promise = (async () => {
    try {
      const token = await getSessionToken()
      return await GatewayService.didGatewayReportAnyUgm(token, gatewayKey)
    } catch (error) {
      console.error('Failed to check if gateway reported any UGM:', error)
      return false
    } finally {
      ugmInFlight.delete(gatewayKey)
    }
  })()

  ugmInFlight.set(gatewayKey, promise)
  return promise
}
